// Minimal Contentful Delivery API client. Shared by the browser bundle and the
// build-time prerender, so it must stay free of DOM access and of the
// Contentful SDK (which drags Buffer/process polyfills into the bundle).

const usePreview = !!process.env.CONTENTFUL_USE_PREVIEW;
const space = process.env.CONTENTFUL_SPACE_ID;
const environment = process.env.CONTENTFUL_ENVIRONMENT_ID || 'master';
const accessToken = usePreview
  ? process.env.CONTENTFUL_PREVIEW_TOKEN
  : process.env.CONTENTFUL_DELIVERY_TOKEN;
const baseUrl =
  process.env.CONTENTFUL_API_BASE_URL ||
  `https://${usePreview ? 'preview' : 'cdn'}.contentful.com`;

export const isConfigured = () => !!(space && accessToken);

const isLink = (value) => value && value.sys && value.sys.type === 'Link';

// The API returns linked entries/assets flat in `includes`; the SDK used to
// stitch them back into `fields`. This does the same, memoised per entry so
// cyclic references cannot recurse forever.
const resolveLinks = (response) => {
  const lookup = new Map();
  const includes = response.includes || {};
  [
    ...(includes.Entry || []),
    ...(includes.Asset || []),
    ...response.items,
  ].forEach((entry) => lookup.set(entry.sys.id, entry));
  const resolved = new Map();

  const resolveEntry = (entry) => {
    if (resolved.has(entry.sys.id)) return resolved.get(entry.sys.id);
    const copy = { sys: entry.sys, fields: {} };
    resolved.set(entry.sys.id, copy);
    Object.entries(entry.fields || {}).forEach(([key, value]) => {
      copy.fields[key] = resolveValue(value);
    });
    return copy;
  };

  const resolveNode = (node) => {
    const next = { ...node };
    if (node.data && isLink(node.data.target)) {
      const target = lookup.get(node.data.target.sys.id);
      next.data = {
        ...node.data,
        target: target ? resolveEntry(target) : null,
      };
    }
    if (Array.isArray(node.content))
      next.content = node.content.map(resolveNode);
    return next;
  };

  const resolveValue = (value) => {
    if (Array.isArray(value)) return value.map(resolveValue).filter(Boolean);
    if (!value || typeof value !== 'object') return value;
    if (isLink(value)) {
      const target = lookup.get(value.sys.id);
      return target ? resolveEntry(target) : null;
    }
    if (value.nodeType === 'document') return resolveNode(value);
    return value;
  };

  return { ...response, items: response.items.map(resolveEntry) };
};

const getEntries = (params) => {
  const query = new URLSearchParams(params).toString();
  const url = `${baseUrl}/spaces/${space}/environments/${environment}/entries?${query}`;
  return fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
    .then((res) => {
      if (!res.ok)
        throw new Error(`Contentful responded ${res.status} for ${url}`);
      return res.json();
    })
    .then(resolveLinks);
};

const parseSections = ({ items }) =>
  items.map(({ fields }) => {
    if (fields.items) {
      return { ...fields, items: parseSections({ items: fields.items }) };
    }
    return { ...fields, image: fields.image ? fields.image.fields : null };
  });

const parseMicrocopy = ({ items }) =>
  items.reduce(
    (acc, { fields }) => ({ ...acc, [fields.key]: fields.value }),
    {}
  );

export const fetchSections = (locale) =>
  getEntries({ content_type: 'sections', include: 10, locale }).then(
    parseSections
  );

export const fetchMicrocopy = (
  locale,
  { excludeLegalContent = false } = {}
) => {
  const params = { content_type: 'microcopy', locale };
  if (excludeLegalContent) {
    params['fields.key[nin]'] =
      'legal.impressum.content,legal.datenschutz.content';
  }
  return getEntries(params).then(parseMicrocopy);
};
